import { Layer } from "./Layer.js";
import { FilterBuilder } from "../filters/FilterBuilder.js";

export { ServiceLayer }

/**
 * Abstract Service Layer Implementation
 * 
 * @author rhess <robin.hess@awi.de>
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * @memberof vef.map.layer
 */
class ServiceLayer extends Layer {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // init default values and define getters and setters
        this.addDefaults_({
            filterType: "",
            name: "",
            // service parameters
            serviceUrl: "",
            serviceSoftware: "",
            serviceVersion: "",
            serviceName: "",
            serviceTitle: "",
            serviceAbstract: "",
            serviceDateTimeStart: "",
            serviceDateTimeEnd: "",
            serviceFees: "",
            serviceAccessConstraints: "",
            serviceKeywords: []
        });

        this.setAll_("type", "service");

        // init getters and setters for configs required in this.config
        this.addConfig_({
            staticQueryParameters: {},
            staticFilter: []
        });

        this.filter = config.filter || [];

        this.timeColumns_ = ["time", "datetime", "date_time", "date_time_start", "date_time_end", "begin_date", "end_date", "dateTimeStart", "dateTimeEnd", "beginDate", "endDate", "DATA_DATETIME"];

        // current filter values of a dimension - to be read by external classes
        this.dimensionValues = {};
        this.attributeValues = {};

        // current messages for possible info about unapplied filters
        this.filterMessages_ = [];
    }

    get capabilitiesUrl() {
        return this.serviceUrl + `?REQUEST=GetCapabilities&SERVICE=${this.type.toUpperCase()}&VERSION=${this.serviceVersion}`;
    }

    /**
     * Get all active messages
     * 
     * @returns {object[]} messages
     */
    getMessages() {
        // implementation of layer-specific messages needs to be done in child classes
        return [...this.messages_, ...this.filterMessages_];
    }

    /**
     * Get Layer name for making requests in case names differ for each projection
     */
    getRequestName() {
        if (typeof this.name == "string") {
            return this.name;
        } else if (typeof this.name == "object") {
            if (this.crs_ in this.name) {
                return this.name[this.crs_];
            } else if ("default" in this.name) {
                return this.name["default"];
            }
        }
        return "";
    }

    /**
     * Iterates through the array of timestamps and returns the
     * one closest to the given value
     * 
     * @param {string} value 
     * @param {number[]} timestamps 
     * @param {string[]} isoTimestamps 
     */
    getClosestTimestamp_(value, timestamps, isoTimestamps) {
        value = new Date(value).getTime();
        let closestIndex = 0;
        let closestDistance = Math.abs(timestamps[0] - value);
        for (let i = 0; i < timestamps.length; ++i) {
            const distance = Math.abs(timestamps[i] - value);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = i;
            }
        }
        return isoTimestamps[closestIndex];
    }

    /**
     * Adjusts time filter values according to the dimension config
     * 
     * @param {object} filter filter values
     * 
     * @private
     */
    adjustTimeValues_(filter) {
        const dimension = (filter.column) ? this.dimensions[filter.column] : null;
        if (!dimension) return filter;

        for (let i = 0; i < filter.values.length; ++i) {
            const values = filter.values[i];
            const originalValues = values.join(", ");

            // override the values if a range is given and set it to a single day
            if (dimension.range == "day") {
                for (let j = 1; j < values.length; ++j) {
                    if (values[0] == "bt") {
                        const day = values[2].substring(0, 10);
                        values[1] = day + "T00:00:00Z";
                        values[2] = day + "T23:59:59Z";
                    }
                }
            }

            // apply timestamp resolution and defaultTime settings
            if (dimension.resolution || dimension.defaultTime) {
                for (let j = 1; j < values.length; ++j) {
                    if (dimension.resolution == "date") {
                        values[j] = values[j].substring(0, 10);
                    } else if (dimension.defaultTime) {
                        values[j] = values[j].substring(0, 10) + "T" + dimension.defaultTime;
                    }
                }
            }

            if (Array.isArray(dimension.values) && (dimension.values.length > 0)) {
                // if values are set, a single value is matched from the list. dimension.mode is ignored
                const timeValues = [];
                for (let i = 0; i < dimension.values.length; ++i) {
                    if (dimension.values[i].length == 24) {
                        timeValues.push(new Date(dimension.values[i]).getTime());
                    }
                }
                const index = (values[0] == "bt") ? 2 : 1;
                filter.values[i] = ["eq", this.getClosestTimestamp_(values[index], timeValues, dimension.values)];
            } else if (Array.isArray(dimension.operators) && (dimension.operators.length > 0)) {
                if (!dimension.operators.includes(values[0])) {
                    const operator = dimension.operators[0];
                    if (operator == "bt") {
                        const begin = values[1].substring(0, 10) + "T00:00:00Z";
                        const end = values[1].substring(0, 10) + "T23:59:59Z";
                        filter.values[i] = [operator, begin, end];
                    } else {
                        const index = (values[0] == "bt") ? 2 : 1;
                        filter.values[i] = [operator, values[index]];
                    }
                }
            }

            let newValues = values.join(", ")
            if (originalValues != newValues) {
                switch (values[0]) {
                    case "bt":
                        newValues = "range from <b>" + values[1] + "</b> to <b>" + values[2] + "</b>";
                        break;
                    case "eq":
                        newValues = "single timestamp <b>" + values[0] + "</b>";
                        break;
                    case "lt":
                        newValues = "less than <b>" + values[0] + "</b>";
                        break;
                    case "gt":
                        newValues = "greater than <b>" + values[0] + "</b>";
                        break;
                    case "lteq":
                        newValues = "less than or equal to <b>" + values[0] + "</b>";
                        break;
                    case "gteq":
                        newValues = "greater than or equal to <b>" + values[0] + "</b>";
                        break;
                }

                this.filterMessages_.push({
                    level: 0,
                    content: "Time filter adjusted to:<br/>" + newValues + "."
                });
            }

        }

        return filter;
    }

    adjustColumnName_(filter, columnNames) {
        let attribute = null;
        for (let i = 0; i < columnNames.length; ++i) {
            if (columnNames[i]) {
                if (columnNames[i] in this.dimensions) {
                    filter.column = columnNames[i];
                    return;
                } else if (!attribute && (columnNames[i] in this.attributeFields)) {
                    attribute = columnNames[i];
                }
            }
        }
        if (attribute) filter.column = attribute;
    }

    /**
     * Gets the filter URL params based on the
     * Filter config and the layer's dimensions
     * @private
     * @param {object[]} config
     */
    createFilterParams_(config) {
        this.filterMessages_ = [];

        if (!Array.isArray(config)) config = [];

        this.dimensionValues = {};
        this.attributeValues = {};

        // merge the static filters and copy config so the original is unchanged
        config = JSON.parse(JSON.stringify(this.staticFilter.concat(config)));

        const params = {};
        const attributeFilters = [];

        for (let f of config) {
            if (f.excludedLayers && f.excludedLayers.includes(this.uniqueId)) {
                this.filterMessages_.push({
                    level: 0,
                    content: "Cannot be filtered by attribute <b>" + f.column + "</b>. This layer is excluded in the filter configuration."
                });
                continue;
            };

            // find and assign the time dimension/atribute
            if (f.type && (f.type.toLowerCase() == "time")) {
                // mapping of possible time column names
                this.adjustColumnName_(f, this.timeColumns_);
                this.adjustTimeValues_(f);
            }

            if (f.column in this.dimensions) {
                const dimension = this.dimensions[f.column];
                // adjust the colum name according to the mapping
                this.dimensionValues[f.column] = f.values;
                f.column = dimension.name || f.column;
                if (!dimension.disabled) {
                    switch (dimension.type) {
                        case "dimension":
                            Object.assign(params, FilterBuilder.getFilterParams([f], this.serviceSoftware, this.type, "dimension", this.name));
                            break;
                        case "attribute_field":
                            attributeFilters.push(f);
                            break;
                    }
                } else {
                    this.filterMessages_.push({
                        level: 0,
                        content: "Cannot be filtered by attribute <b>" + f.column + "</b>, because it is disabled in the layer configuration."
                    });
                }
            } else if (f.column in this.attributeFields) {
                const field = this.attributeFields[f.column];
                this.attributeValues[f.column] = f.values;
                if (!field.disabled) {
                    attributeFilters.push(f);
                } else {
                    this.filterMessages_.push({
                        level: 0,
                        content: "Cannot be filtered by attribute <b>" + f.column + "</b>, because it is disabled in the layer configuration."
                    });
                }
            } else if (!f.column && Array.isArray(f.values)) {
                // syntax [operator, column, values, ...] not supported for type "dimension"
                const values = [];
                for (let i = 0; i < f.values.length; ++i) {
                    const col = f.values[i][1];
                    if ((col in this.dimensions) && (this.dimensions[col].type == "attribute_field")) {

                        if (col in this.dimensionValues) {
                            this.dimensionValues[col].push(f.values[i]);
                        } else {
                            this.dimensionValues[col] = [f.values[i]];
                        }

                        if (!this.dimensions[col].disabled) {
                            // adjust the colum name according to the mapping
                            f.values[i][1] = this.dimensions[col].name || col;
                            values.push(f.values[i]);
                        } else {
                            this.filterMessages_.push({
                                level: 0,
                                content: "Cannot be filtered by attribute <b>" + f.column + "</b>, because it is disabled in the layer configuration."
                            });
                        }
                    } else if (col in this.attributeFields) {
                        if (col in this.attributeValues) {
                            this.attributeValues[col].push(f.values[i]);
                        } else {
                            this.attributeValues[col] = [f.values[i]];
                        }

                        if (!this.attributeFields[col].disabled) {
                            values.push(f.values[i]);
                        } else {
                            this.filterMessages_.push({
                                level: 0,
                                content: "Cannot be filtered by attribute <b>" + f.column + "</b>, because it is disabled in the layer configuration."
                            });
                        }
                    } else {
                        this.filterMessages_.push({
                            level: 0,
                            content: "Cannot be filtered by attribute <b>" + f.column + "</b>."
                        });
                    }
                }
                if (values.length) attributeFilters.push({
                    type: f.type,
                    column: null,
                    excludedLayers: f.excludedLayers,
                    values: values
                });
            } else {
                this.filterMessages_.push({
                    level: 0,
                    content: "Cannot be filtered by attribute <b>" + f.column + "</b>."
                });
            }

        }

        Object.assign(params, FilterBuilder.getFilterParams(attributeFilters, this.serviceSoftware, this.type, this.filterType, this.name));

        this.fire("message", this.getMessages())

        // merge the params with static parameters
        return Object.assign(params, this.staticQueryParameters);
    }

}
